<template>
  <div class="container mx-auto px-4 py-8">
    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p class="ml-4">Loading vehicle details and redirecting...</p>
    </div>
    
    <div v-else-if="error" class="bg-destructive/10 text-destructive p-4 rounded-lg">
      <h2 class="text-xl font-semibold mb-2">Error Finding Vehicle</h2>
      <p>Could not find vehicle details for ID: {{ id }}. Please check the ID or try browsing.</p>
      <pre v-if="error.message" class="text-sm mt-2">Error: {{ error.message }}</pre>
      <NuxtLink 
        to="/vehicles" 
        class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition mt-4"
      >
        View All Vehicles
      </NuxtLink>
    </div>
    
    <div v-else class="py-12 text-center">
      <p class="text-muted-foreground">
        Redirecting to vehicle details...
      </p>
       <NuxtLink 
        to="/vehicles" 
        class="inline-flex items-center px-4 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/90 transition mt-4"
      >
        Or click here to View All Vehicles
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehiclesStore } from '~/stores/vehicles'

const route = useRoute()
const vehiclesStore = useVehiclesStore()
const id = route.params.id as string

// Use useAsyncData to fetch minimal data needed for redirect OR find in store
const { data: redirectInfo, pending: loading, error } = await useAsyncData(
  `redirect-vehicle-${id}`, 
  async () => {
    if (!id) return { found: false, slug: null, error: 'No ID provided' };

    // 1. Check store first (most common case when navigating)
    let foundVehicle = vehiclesStore.vehicles.find(v => v.id === id || v.stockid === id);
    if (foundVehicle) {
      return { found: true, slug: foundVehicle.slug };
    }
    
    // 2. If not in store, fetch minimal data (assuming endpoint exists/is efficient)
    try {
      // Ideally, this endpoint returns just { id, slug } or similar minimal data
      const minimalData = await $fetch<any>(`/api/vehicles/${id}`);
      
      if (minimalData && !minimalData.error) {
        return { found: true, slug: minimalData.slug };
      } else {
         return { found: false, slug: null, error: minimalData?.message || 'Vehicle not found via API' };
      }
    } catch (fetchErr: any) {
      return { found: false, slug: null, error: fetchErr.message || 'Fetch failed' };
    }
  },
  {
    server: true, // Allow SSR fetch
    lazy: false, // Need data before trying to redirect
  }
);

// Perform redirect once data is available
watch(redirectInfo, (info) => {
  if (!info) return; // Wait for data

  if (info.found) {
    const targetSlug = info.slug || 'details'; // Fallback slug
    navigateTo(`/vehicle-for-sale/${id}/${targetSlug}`, { replace: true, redirectCode: 301 });
  } else {
    // Error state is handled by the template using the 'error' ref from useAsyncData
  }
}, { immediate: true }); // Run watcher immediately
</script>